import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImportExtractionStatus } from "../contracts/ImportExtractionStatus";

const FINDING_AFTER_SECONDS = 15;
const SLOW_AFTER_SECONDS = 60;

interface ExtractionProgressProps {
  /** Null while the material is still uploading. */
  status: ImportExtractionStatus | null;
}

function stageKey(status: ImportExtractionStatus | null, seconds: number): string {
  if (status === null) return "uploading";
  if (seconds >= SLOW_AFTER_SECONDS) return "slow";
  if (seconds >= FINDING_AFTER_SECONDS) return "finding";
  return "reading";
}

/**
 * What the instructor sees while the extraction runs in the background: the current step,
 * the seconds so far, and that leaving the page cancels the import.
 */
export function ExtractionProgress({ status }: ExtractionProgressProps) {
  const { t } = useTranslation();
  const [seconds, setSeconds] = useState(0);
  const container = useRef<HTMLDivElement>(null);

  // On a laptop screen the form pushes this below the fold; bring it into view once.
  useEffect(() => {
    container.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div ref={container} className="mt-4 flex items-start gap-3">
      <span
        className="mt-0.5 animate-spin rounded-full h-5 w-5 shrink-0 border-2 border-primary-40 border-t-transparent"
        aria-hidden="true"
      />
      <div>
        <p className="text-grey-20 dark:text-white" role="status" aria-live="polite">
          {t(`assistantImport.source.progress.${stageKey(status, seconds)}`)}
        </p>
        <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">
          <span className="tabular-nums">{t("assistantImport.source.progress.elapsed", { count: seconds })}</span>
          {" · "}
          {t("assistantImport.source.extractingHint")}
        </p>
      </div>
    </div>
  );
}
